import { Button, ButtonGroup, Heading, Modal, Text, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { useRouter } from 'next/router';
import type {
  RentalApplication,
  WithdrawApplicationMutation,
  WithdrawSharedApplicationMutation,
} from '../../graphql/queries';
import { logError } from '@zg-rentals/log-error';
import { emitWithdrawConfirmEvent } from '../../analytics/clickstreamEvents';
import { useWithdrawApplication } from '../../hooks/useWithdrawApplication';
import { useApplicationContextOrUndefined } from '../../contexts/ApplicationContext';

interface WithdrawApplicationProps {
  application: RentalApplication;
  isOpen: boolean;
  onCloseModal: () => void;
}

export function WithdrawApplication({ application, isOpen, onCloseModal }: WithdrawApplicationProps) {
  const applicationId = application.id as string;
  const isPrimaryApplicant = application.isPrimaryApplicant;
  const street =
    application.rentalListing?.address?.street || application.applicationShareInformation?.propertyAddress?.street;
  const unit =
    application.rentalListing?.address?.unit || application.applicationShareInformation?.propertyAddress?.unit;
  const streetAddress = `${street}${unit ? ` ${unit}` : ''}`;
  const hasListing = !!application.rentalListing;

  const [withdrawApplicationMutation, { loading }] = useWithdrawApplication(hasListing);

  const router = useRouter();
  const { enqueueToast } = useToast();
  const applicationContext = useApplicationContextOrUndefined();

  async function withdrawApplication() {
    emitWithdrawConfirmEvent(applicationContext);
    try {
      const response = await withdrawApplicationMutation({
        variables: {
          applicationId,
        },
      });

      if (
        (response?.data as WithdrawApplicationMutation)?.withdrawApplication ||
        (response?.data as WithdrawSharedApplicationMutation)?.withdrawSharedApplication
      ) {
        enqueueToast(<Toast tone="info">Application withdrawn</Toast>);
        onCloseModal();
        await router.push('/');
      } else {
        enqueueToast(<Toast tone="critical">We couldn’t withdraw your application. Try again.</Toast>);
        onCloseModal();
      }
    } catch (err) {
      logError({ error: err, context: 'WithdrawApplication - withdrawApplicationMutation error' });
      enqueueToast(<Toast tone="critical">We couldn’t withdraw your application. Try again.</Toast>);
      onCloseModal();
    }
  }

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onCloseModal()}
      size={{ base: 'fullScreen', md: 'sm' }}
      header={
        <Heading level={2}>{isPrimaryApplicant ? `Withdraw application` : `Contact the primary applicant`}</Heading>
      }
      body={
        <>
          {isPrimaryApplicant && (
            <Heading level={2} textStyle="body-lg-bold" css={{ marginBottom: 'tighter' }}>
              Withdraw application{streetAddress ? ` for ${streetAddress}` : ''}?
            </Heading>
          )}
          <Text>
            {isPrimaryApplicant
              ? `When you withdraw an application, the landlord will immediately lose access to all personal information on the
          application. If you want to update this application, you can make changes and submit it again, even to the
          same landlord.`
              : `Because you were added as a co-applicant on someone else’s application, you’re not able to withdraw your application separately. The person who added you will need to withdraw their application, which will also withdraw yours.`}
          </Text>
        </>
      }
      footer={
        isPrimaryApplicant ? (
          <ButtonGroup aria-label="withdraw application">
            <TextButton onClick={onCloseModal} disabled={loading}>
              No, go back
            </TextButton>
            <Button tone="critical" emphasis="filled" onClick={withdrawApplication} disabled={loading}>
              Yes, withdraw
            </Button>
          </ButtonGroup>
        ) : (
          <Button tone="brand" emphasis="filled" onClick={onCloseModal}>
            OK
          </Button>
        )
      }
    />
  );
}
